/**
 * 10.9 — LOBBY PvP (presença via Supabase Realtime). Mostra quem está ONLINE no lobby agora.
 * É a Fase 2 do PvP (presença + matchmaking). O duelo em si (Fase 3) vem depois.
 *
 * Presença é efêmera (não usa banco): cada jogador "track"a {id,name,level,power} num canal
 * compartilhado; todos recebem a lista sincronizada. Tolerante: sem internet, lista vazia.
 */
import { supabase } from "./supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";

export interface LobbyPlayer {
  id: string;
  name: string;
  level: number;
  power: number;
  searching?: boolean;
  /** 10.9BC — fila de qual modo ("1v1" padrão; "3v3" = times de 3). */
  mode?: "1v1" | "3v3";
  /** 10.9FF — instante em que entrou na fila: entra no nome do canal da partida (cada luta = canal novo) */
  q?: number;
  /** 10.9FF — casei com este id: fico visivel uns segundos pro rival tambem entrar na luta */
  vs?: string;
}

let channel: RealtimeChannel | null = null;
let myId: string | null = null;

async function uid(): Promise<string | null> {
  if (myId) return myId;
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      myId = data.session.user.id;
      return myId;
    }
    const r = await supabase.auth.signInAnonymously();
    myId = r.data.user?.id ?? null;
    return myId;
  } catch {
    return null;
  }
}

let myQ = 0; // 10.9FF — minha marca de fila atual
export function selfQ(): number { return myQ; }
export function selfId(): string | null {
  return myId;
}

/** Entra no lobby e recebe a lista de online sempre que mudar. */
export async function joinLobby(
  me: {
  name: string; level: number; power: number },
  onChange: (players: LobbyPlayer[]) => void,
): Promise<void> {
  myQ = 0; // 10.9GO — nova entrada no lobby = marca nova (revanche nao reusa o canal da luta anterior)
  await leaveLobby();
  const id = await uid();
  if (!id) {
    onChange([]);
    return;
  }
  const ch = supabase.channel("pvp-lobby", { config: { presence: { key: id } } });
  channel = ch;
  const emit = (): void => {
    const state = ch.presenceState() as unknown as Record<string, LobbyPlayer[]>;
    const players: LobbyPlayer[] = [];
    for (const arr of Object.values(state)) {
      // 10.9FF — a mesma pessoa pode ter registro VELHO (conexao anterior) no lobby: usa o mais novo
      let p: LobbyPlayer | undefined;
      for (const x of arr ?? []) if (x && x.id && (!p || (x.q ?? 0) >= (p.q ?? 0))) p = x;
      if (p) players.push(p);
    }
    onChange(players);
  };
  ch.on("presence", { event: "sync" }, emit);
  ch.on("presence", { event: "join" }, emit);
  ch.on("presence", { event: "leave" }, emit);
  ch.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      void ch.track({ id, name: me.name || "Caçador", level: Math.floor(me.level), power: Math.floor(me.power), searching: false });
    }
  });
}

/** Marca/desmarca "procurando oponente" (aparece pros outros no lobby). */
export async function setSearching(me: { name: string; level: number; power: number }, searching: boolean, mode: "1v1" | "3v3" = "1v1", vs?: string): Promise<void> {
  const id = myId;
  if (!channel || !id) return;
  try {
    // 10.9FF — a marca so muda ao ENTRAR na fila; ao sair (casou) ela fica, pro nome do canal bater nos dois lados
    // 10.9FH — a marca (q) so nasce ao ENTRAR na fila e vive ate SAIR: confirmar o par (1x1 ou 3x3)
    // nunca a renova, senao o nome do canal (que usa q) fica diferente entre os clientes.
    if (!searching) myQ = 0;
    else if (myQ === 0) myQ = Date.now();
    void vs;
    await channel.track({ id, name: me.name || "Caçador", level: Math.floor(me.level), power: Math.floor(me.power), searching, mode, q: myQ, vs });
  } catch {
    /* ignora */
  }
}

export async function leaveLobby(): Promise<void> {
  if (channel) {
    try {
      await supabase.removeChannel(channel);
    } catch {
      /* ignora */
    }
    channel = null;
  }
}
